import { useMemo, useState, useEffect } from "react";
import CustomerUpdateInput from "./CustomerInput";
import CustomerAutocomplete from "./CustomerAutocomplete";
import { Button, Checkbox, FormControlLabel, Grid, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import useCustomerAddressCreate from "src/hooks/user/customer/useCustomerAddressCreate";
import useCustomerAddressUpdate from "src/hooks/user/customer/useCustomerAddressUpdate";
import useCustomAddressDetail from "src/hooks/user/customer/useCustomAddressDetail";
import CustomerAddressItem from "./CustomerAddressItem";
import { enqueueSnackbar } from "notistack";
import useCustomAddressRemove from "src/hooks/user/customer/useCustomAddressRemove";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { checkVietnam, getAddressIdsFromForm, mapToAddressOption, mapToOptions } from "./utils/mapping";
import FieldLabel from "./FieldLabel";
import { useAddressData } from "./utils/useAddressData";
import PhoneNumberInput from "src/components/shared/PhoneNumberInput";
const phoneRegExp = /^\+{0,1}[0-9]{9,15}$/;
const schema = yup.object().shape({
  recipientName: yup.string().required("Vui lòng nhập tên người nhận"),
  phone: yup.string().matches(phoneRegExp, "Số điện thoại không hợp lệ").required("Vui lòng nhập số điện thoại"),
  detailAddress: yup.string().required("Vui lòng nhập địa chỉ cụ thể"),
  addressLevel: yup.number().optional(),
  country: yup
    .object({
      value: yup.string().required("Vui lòng chọn quốc gia"),
    })
    .required("Vui lòng chọn quốc gia"),
  province: yup.object().when("country", {
    is: (country) => checkVietnam(country),
    then: (schema) => schema.required("Vui lòng chọn thành phố/ tỉnh"),
    otherwise: (schema) => schema.notRequired(),
  }),
  district: yup.object().when(["addressLevel", "country"], {
    is: (addressLevel, country) => addressLevel === 3 && checkVietnam(country),
    then: (schema) => schema.required("Vui lòng chọn quận/ huyện"),
    otherwise: (schema) => schema.notRequired(),
  }),
  ward: yup.object().when("country", {
    is: (country) => checkVietnam(country),
    then: (schema) => schema.required("Vui lòng chọn phường/ xã"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const CustomerUpdateAddress = ({ item, onOpenDialog, onCloseDialog, addressList, setAddressList }) => {
  const [activePhoneCode, setActivePhoneCode] = useState(null);
  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      recipientName: "",
      phone: "",
      detailAddress: "",
      country: null,
      province: null,
      district: null,
      ward: null,
      isDefault: false,
      addressLevel: 2,
      _id: undefined,
    },
  });

  const country = form.watch("country");
  const province = form.watch("province");
  const district = form.watch("district");
  const addressLevel = form.watch("addressLevel");
  const { countries, provinces, districts, wards } = useAddressData(province, district, addressLevel);
  const { data: details, detailLoading } = useCustomAddressDetail(item?._id);

  const { trigger: createMoreAddress } = useCustomerAddressCreate(item?._id);
  const { trigger: updateAddress } = useCustomerAddressUpdate(item?._id);
  const { trigger: removeAddress } = useCustomAddressRemove(item?._id);

  const handleAddressRemove = async ({ addressId, id }) => {
    const res = await removeAddress({
      pathIncluded: {
        customerId: id,
        addressId: addressId,
      },
    });
    if (res?.success) {
      onCloseDialog();
      return enqueueSnackbar("Thành công", { variant: "success" });
    }
    return enqueueSnackbar(res?.error?.message ?? "Có lỗi xảy ra", { variant: "error" });
  };

  const handleGetDetail = (address) => {
    const { country, ward, district, province } = mapToAddressOption(address);
    form.reset({
      recipientName: address?.recipientName,
      phone: address?.phone,
      detailAddress: address?.detailAddress,
      isDefault: address?.isDefault,
      addressLevel: address?.ward?.district?._id ? 3 : 2,
      _id: address?._id,
      province,
      ward,
      district,
      country,
    });
  };

  const handleCreateMoreAddress = async (values) => {
    try {
      let res;
      const addressIds = getAddressIdsFromForm(values);
      const body = {
        ...values,
        ...addressIds,
        country: values?.country?.id,
      };
      if (!values?._id) {
        res = await createMoreAddress({
          body,
        });
      } else {
        res = await updateAddress({
          pathIncluded: {
            customerId: item?._id,
            addressId: values?._id,
          },
          body,
        });
      }

      if (res?.success) {
        form.reset({
          recipientName: "",
          phone: "",
          detailAddress: "",
          country: null,
          province: null,
          district: null,
          ward: null,
          isDefault: false,
          addressLevel: 3,
          _id: undefined,
        });
        return enqueueSnackbar("Thành công", { variant: "success" });
      }
      return enqueueSnackbar(res?.error?.message ?? "Có lỗi xảy ra", { variant: "error" });
    } catch (error) {
      return enqueueSnackbar(error?.message ?? "Có lỗi xảy ra", { variant: "error" });
    }
  };

  useEffect(() => {
    const nextPhoneCode = countries?.data?.find((v) => v._id === country?.id)?.phoneCode ?? null;
    setActivePhoneCode((prev) => (prev === nextPhoneCode ? prev : nextPhoneCode));
  }, [countries?.data, country]);

  const isVietnam = useMemo(() => checkVietnam(country), [country]);

  return (
    <>
      {/* ADDRESS LIST */}
      <Grid
        item
        container
        sx={{ maxHeight: "220px", height: "80%", overflow: "auto", mt: "24px" }}
      >
        {Array.isArray(details?.data) ? (
          details?.data?.map((v) => {
            return (
              <CustomerAddressItem
                key={v?._id}
                v={v}
                detailLoading={detailLoading}
                handleOpenDialog={onOpenDialog}
                handleAddressRemove={handleAddressRemove}
                handleGetDetail={handleGetDetail}
              />
            );
          })
        ) : (
          <Typography>Không có dữ liệu</Typography>
        )}
      </Grid>
      {/* INPUT ADD DETAIL */}
      <FormProvider {...form}>
        <Grid
          container
          spacing={2}
        >
          <CustomerUpdateInput
            name="recipientName"
            label="Tên người nhận"
            mt="24px"
          />
          <CustomerAutocomplete
            name="country"
            label="Quốc gia"
            options={mapToOptions(countries)}
            xs={12}
            onChange={() => {
              form.setValue("province", null);
              form.setValue("district", null);
              form.setValue("ward", null);
              form.setValue("addressLevel", 3);
            }}
          />
          <Grid
            item
            xs={12}
          >
            <PhoneNumberInput
              name="phone"
              label="Số điện thoại"
              width="100%"
              control={form.control}
              phoneCode={activePhoneCode}
            />
          </Grid>
          <CustomerUpdateInput
            name="detailAddress"
            label="Địa chỉ cụ thể"
            placeholder="Địa chỉ cụ thể"
          />
          {isVietnam && (
            <>
              <Controller
                name="addressLevel"
                control={form.control}
                render={({ field }) => {
                  return (
                    <Grid
                      item
                      xs={12}
                    >
                      <RadioGroup
                        row
                        value={field.value}
                        onChange={(e) => {
                          form.setValue("district", null);
                          form.setValue("ward", null);
                          form.setValue("province", null);
                          field.onChange(Number(e.target.value));
                        }}
                      >
                        <FormControlLabel
                          value={2}
                          control={<Radio />}
                          label={<FieldLabel label="Địa chỉ 2 cấp" />}
                        />
                        <FormControlLabel
                          value={3}
                          control={<Radio />}
                          label={<FieldLabel label="Địa chỉ 3 cấp" />}
                        />
                      </RadioGroup>
                    </Grid>
                  );
                }}
              />
              <CustomerAutocomplete
                name="province"
                label="Thành phố/ Tỉnh"
                options={mapToOptions(provinces)}
                xs={12 / addressLevel}
              />
              {addressLevel === 3 && (
                <CustomerAutocomplete
                  name="district"
                  label="Quận/ Huyện"
                  options={mapToOptions(districts)}
                  xs={12 / addressLevel}
                />
              )}
              <CustomerAutocomplete
                name="ward"
                label="Phường/ Xã"
                options={mapToOptions(wards)}
                xs={12 / addressLevel}
                disabled={!province}
              />
            </>
          )}
          <Grid
            item
            xs={12}
          >
            <Controller
              name="isDefault"
              control={form.control}
              render={({ field }) => {
                return (
                  <Stack
                    direction="row"
                    alignItems="center"
                  >
                    <Checkbox
                      checked={field.value}
                      onChange={() => field.onChange(!field.value)}
                      sx={{ ml: -1.5 }}
                    />
                    <Typography>Mặc định</Typography>
                  </Stack>
                );
              }}
            />
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          align="right"
        >
          <Button
            variant="outlined"
            // onClick={form.handleSubmit(handleCreateMoreAddress)}
          >
            Thêm địa chỉ
          </Button>
        </Grid>
      </FormProvider>
    </>
  );
};

export default CustomerUpdateAddress;
